/**
 * The matchup board as the player reads it (PRD S5): the seven battles across the line from the
 * sim's `BOARD`, and the player's win chance from the same `offenseWinChance` the AI weighs, so the
 * board shows exactly what the AI reads. Slice 8d: each row also names the player's side (`you`,
 * always the left column) and the other (`them`), and carries one reason line in words
 * (`edgeReason`, game/reason.ts).
 *
 * SUB options (slice 8b, PRD S5): every legal bench Pokémon for the player's slot on a row, with
 * the win chance it would have there, as the AI's sub window scores it (`sideWinChance` with
 * `atSlot(c, slot, true)`: a sub's stages do not travel). Two groups: those who flip the row to the
 * player (`p <= 0.5 < q`), best first, then everyone else, freshest first, for rotation.
 *
 * Slice 8c: every side carries its HP; MOVE options swap the row's Pokémon with another on the
 * field (the sim's swap path in `applyWindow`: a sub whose Pokémon is already in the chart, stages
 * kept); the Bench strip lists the bench.
 *
 * Slice 8d: the bench is per side. Pokémon play both ways, so a starter benched on offense is still
 * on the defense chart; the offense's bench is everyone not in the offense chart (the sim's own
 * rule: `applyWindow` takes him back, and `afterSnap` heals him while the offense plays).
 */
import {
  BOARD,
  DEFENSE_SLOTS,
  OFFENSE_SLOTS,
  atSlot,
  lineOf,
  lineupOf,
  offenseWinChance,
  opponentOf,
  sideWinChance,
  type BoardKind,
  type Combatant,
  type DefenseSlot,
  type Game,
  type OffenseSlot,
  type Position,
  type Status,
  type Team,
  type TeamId,
  type TypeName,
} from '@tdmons/sim';
import { edgeReason, type EdgeReason } from './reason';

export interface BoardSide {
  slot: OffenseSlot | DefenseSlot;
  name: string;
  types: readonly TypeName[];
  team: TeamId;
  hp: number;
  maxHp: number;
}

export interface BoardRow {
  kind: BoardKind;
  offense: BoardSide;
  defense: BoardSide;
  /** The player's side in play; `you` is the player's Pokémon on the row, `them` the other. */
  side: 'offense' | 'defense';
  you: BoardSide;
  them: BoardSide;
  /** The player's chance to win this battle as the two stand (no spent move). */
  chance: number;
  /** The one reason line: the factor that decides the row, in words. */
  reason: EdgeReason;
}

const side = (slot: OffenseSlot | DefenseSlot, c: Combatant, team: TeamId): BoardSide => ({
  slot,
  name: c.name,
  types: c.types,
  team,
  hp: c.hp,
  maxHp: c.stats.hp,
});

export function boardRows(game: Readonly<Game>, player: TeamId): BoardRow[] {
  const o = game.possession;
  const d: TeamId = o === 'A' ? 'B' : 'A';
  const offense = lineupOf(game.teams[o], game.teams[o].offense);
  const defense = lineupOf(game.teams[d], game.teams[d].defense);
  const onOffense = player === o;
  const seat = onOffense ? 'offense' : 'defense';
  return BOARD.map((line) => {
    const a = offense[line.offense];
    const b = defense[line.defense];
    const p = offenseWinChance(line.kind, a, b);
    const off = side(line.offense, a, o);
    const def = side(line.defense, b, d);
    return {
      kind: line.kind,
      offense: off,
      defense: def,
      side: seat,
      you: onOffense ? off : def,
      them: onOffense ? def : off,
      chance: onOffense ? p : 1 - p,
      reason: edgeReason(line.kind, a, b, seat),
    };
  });
}

/** A bench Pokémon the player can send in at one slot, and the chance it would have there. */
export interface SubOption {
  index: number;
  name: string;
  types: readonly TypeName[];
  hp: number;
  maxHp: number;
  status?: Status;
  /** The player's win chance on the row with this Pokémon in the slot. */
  chance: number;
}

export interface SubOptions {
  /** The row's current chance for the player. */
  chance: number;
  /** Turn the row to the player: `chance <= 0.5 < q`, highest `q` first. */
  flips: SubOption[];
  /** Every other legal bench Pokémon, by HP share, highest first (rotation for fatigue). */
  rest: SubOption[];
}

type Side = 'offense' | 'defense';

/** Roster indices in the chart of `side`: the side's field. */
const fieldOf = (team: Team, side: Side): Set<number> => new Set(Object.values(team[side]));

/**
 * The bench a sub can come from for `side`: not in that side's chart, above 0 HP, not sitting out
 * Rest, and not drafted to spend Rest this window (Rest is spent on the bench, after the subs).
 */
export function legalBench(team: Team, side: Side, resting: readonly number[] = []): number[] {
  const on = fieldOf(team, side);
  return team.roster.flatMap((c, i) =>
    on.has(i) || c.hp <= 0 || (c.sitPlays ?? 0) > 0 || resting.includes(i) ? [] : [i],
  );
}

/** The SUB list for the player's `slot` (game: the previewed personnel). */
export function subOptions(
  game: Readonly<Game>,
  player: TeamId,
  slot: OffenseSlot | DefenseSlot,
  resting: readonly number[] = [],
): SubOptions {
  const side = game.possession === player ? 'offense' : 'defense';
  const other: TeamId = player === 'A' ? 'B' : 'A';
  const team = game.teams[player];
  const ours = side === 'offense' ? lineupOf(team, team.offense) : lineupOf(team, team.defense);
  const foe = game.teams[other];
  const theirs = side === 'offense' ? lineupOf(foe, foe.defense) : lineupOf(foe, foe.offense);
  const line = lineOf(slot);
  const opponent = (theirs as Record<string, Combatant>)[opponentOf(slot)] as Combatant;
  const starter = (ours as Record<string, Combatant>)[slot] as Combatant;
  const p = sideWinChance(side, line.kind, starter, opponent);
  const options = legalBench(team, side, resting).map((index): SubOption => {
    const c = team.roster[index] as Combatant;
    return {
      index,
      name: c.name,
      types: c.types,
      hp: c.hp,
      maxHp: c.stats.hp,
      ...(c.status ? { status: c.status.kind } : {}),
      chance: sideWinChance(side, line.kind, atSlot(c, slot, true), opponent),
    };
  });
  const flips = p <= 0.5 ? options.filter((o) => o.chance > 0.5) : [];
  const rest = options.filter((o) => !flips.includes(o));
  // Stable sorts: ties keep roster order.
  flips.sort((x, y) => y.chance - x.chance);
  rest.sort((x, y) => y.hp / y.maxHp - x.hp / x.maxHp);
  return { chance: p, flips, rest };
}

type Slot = OffenseSlot | DefenseSlot;

/** The player's side in play, its lineup, and the opponent's. */
function seats(game: Readonly<Game>, player: TeamId) {
  const side = game.possession === player ? 'offense' : 'defense';
  const other: TeamId = player === 'A' ? 'B' : 'A';
  const team = game.teams[player];
  const chart = (side === 'offense' ? team.offense : team.defense) as Record<Slot, number>;
  const ours: Record<string, Combatant> = lineupOf(team, chart);
  const foe = game.teams[other];
  const theirs = (
    side === 'offense' ? lineupOf(foe, foe.defense) : lineupOf(foe, foe.offense)
  ) as Record<string, Combatant>;
  return { side, chart, ours, theirs } as const;
}

/** Another of the player's on-field Pokémon that could swap into a row's slot. */
export interface MoveOption {
  /** The slot it is in now, and its roster index. */
  slot: Slot;
  index: number;
  name: string;
  types: readonly TypeName[];
  hp: number;
  maxHp: number;
  /** The player's win chance on the row with this Pokémon in its slot (stages kept: a swap). */
  chance: number;
}

/**
 * MOVE for the player's `slot` (game: the previewed personnel): every other on-field Pokémon of
 * the side in play, in slot order, except slots in `busy` (a drafted sub or swap already there).
 */
export function moveOptions(
  game: Readonly<Game>,
  player: TeamId,
  slot: Slot,
  busy: ReadonlySet<Slot> = new Set(),
): MoveOption[] {
  const { side, chart, ours, theirs } = seats(game, player);
  const line = lineOf(slot);
  const opponent = theirs[opponentOf(slot)] as Combatant;
  const slots: readonly Slot[] = side === 'offense' ? OFFENSE_SLOTS : DEFENSE_SLOTS;
  return slots
    .filter((s) => s !== slot && !busy.has(s))
    .map((s) => {
      const c = ours[s] as Combatant;
      return {
        slot: s,
        index: chart[s],
        name: c.name,
        types: c.types,
        hp: c.hp,
        maxHp: c.stats.hp,
        chance: sideWinChance(side, line.kind, atSlot(c, slot), opponent),
      };
    });
}

/** A bench Pokémon in the Bench strip. */
export interface BenchView {
  index: number;
  name: string;
  types: readonly TypeName[];
  hp: number;
  maxHp: number;
  status?: Status;
  /** The positions it is listed for; null when it fits anywhere. */
  positions: readonly Position[] | null;
  /** Plays left sitting out Rest. */
  sitPlays: number;
  /** A two-way player: a starter on the other side's chart (subbing him in keeps him there). */
  alsoOn?: Side;
}

/**
 * The player's bench for the side in play: everyone not in that side's chart, in roster order
 * (game: the previewed personnel). Rest and 0 HP stay listed; the strip says so.
 */
export function benchViews(game: Readonly<Game>, player: TeamId): BenchView[] {
  const team = game.teams[player];
  const side: Side = game.possession === player ? 'offense' : 'defense';
  const other: Side = side === 'offense' ? 'defense' : 'offense';
  const on = fieldOf(team, side);
  const there = fieldOf(team, other);
  return team.roster.flatMap((c, index): BenchView[] =>
    on.has(index)
      ? []
      : [
          {
            index,
            name: c.name,
            types: c.types,
            hp: c.hp,
            maxHp: c.stats.hp,
            ...(c.status ? { status: c.status.kind } : {}),
            positions: c.positions && c.positions.length > 0 ? c.positions : null,
            sitPlays: c.sitPlays ?? 0,
            ...(there.has(index) ? { alsoOn: other } : {}),
          },
        ],
  );
}
